import { FC } from "react";
import { Card, CopyableInput, DataTable } from "@ventumsoftware/react-toolkit";
import { SectionHeader } from "@hostingbv/lib/src/components/content/SectionHeader";
import {
  MailAliasDeploymentDetails,
  MailBoxDeploymentDetails,
  MailDomain,
  MailDomainDeploymentDetails,
  MailForwardDeploymentDetails,
} from "@hostingbv/lib/src/api/partner/models/MailDomain";
import {
  faArrowRight,
  faKey,
  faMailForward,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { Paragraph } from "../../../lib/src/components/Paragraph";
import { Copyable } from "../../../lib/src/components/content/Copyable";
import { HoverHint } from "../../../lib/src/components/content/HoverHint";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ContextMenu } from "../../../lib/src/components/content/ContextMenu";
import { usePermissions } from "../../../lib/src/auth/permissions";
import { Editor } from "../../../lib/src/components/dns-editor/Editor";
import { Domain } from "../../../lib/src/api/partner/models/Domain";
import { useModal } from "../../../lib/src/components/layout/ModalProvider";
import { useApiClient } from "@hostingbv/lib/src/api";
import { config } from "../config";
import { AddMailUserModal } from "../modals/AddMailUserModal";
import { DeleteMailUserModal } from "../modals/DeleteMailUserModal";
import { EditMailUserModal } from "../modals/EditMailUserModal";
import { DeleteMailAliasModal } from "../modals/DeleteMailAliasModal";
import { DeleteMailForwardModal } from "../modals/DeleteMailForwardModal";
import { AddMailAliasOrForwardModal } from "../modals/AddMailAliasOrForwardModal";

export const ImapMailDomainDetails: FC<{ mailDomain: MailDomain }> = ({ mailDomain }) => {
  return (
    <>
      <div className={"flex-1 mt-8"}>
        <SectionHeader>Users</SectionHeader>
        <UsersOverview mailDomain={mailDomain} />
      </div>
      <div className={"flex-1"}>
        <SectionHeader>Forwards and Aliases</SectionHeader>
        <ForwardsAliasesOverview mailDomain={mailDomain} />
      </div>
      <div className={"-mb-4"}>
        <SectionHeader>Connection information</SectionHeader>
      </div>
      <div className={"grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8"}>
        <Card title={"Incoming mail"}>
          <Paragraph>Configure this in your preferred e-mail client on your phone, tablet or computer.</Paragraph>
          <ul className={"list-disc pl-4 leading-loose text-slate-800"}>
            <li>
              Server: <Copyable text={"mail.infrapod.nl"} />
            </li>
            <li>Username: Your e-mail address</li>
            <li>Password: Your password</li>
            <li>
              Protocol: <strong>IMAP (SSL)</strong>
            </li>
            <li>
              Protocol: <Copyable text={"993"} />
            </li>
          </ul>
        </Card>
        <Card title={"Outgoing mail"}>
          <Paragraph>Configure this in your preferred e-mail client on your phone, tablet or computer.</Paragraph>
          <ul className={"list-disc pl-4 leading-loose text-slate-800"}>
            <li>
              Server: <Copyable text={"mail.infrapod.nl"} />
            </li>
            <li>Username: Your e-mail address</li>
            <li>Password: Your password</li>
            <li>
              Protocol: <strong>SMTP (TLS)</strong>
            </li>
            <li>
              Protocol: <Copyable text={"587"} />
            </li>
          </ul>
        </Card>
        <div className={"col-span-2"}>
          <Card title={"DNS"}>
            <Paragraph>
              To send and receive mail, you need to add the following DNS records to the DNS of the domain name.
            </Paragraph>
            <Editor
              domain={{ displayName: mailDomain.displayName } as Domain}
              records={[
                { id: "0", type: "MX", name: "", content: "filter01.mail.infrapod.nl", ttl: 3600, priority: 10 },
                { id: "0", type: "MX", name: "", content: "filter02.mail.infrapod.nl", ttl: 3600, priority: 20 },
                { id: "0", type: "TXT", name: "", content: "v=spf1 include:_spf.infrapod.nl -all", ttl: 3600 },
              ]}
              readonlyMode={true}
              onSave={() => {}}
            />
          </Card>
        </div>
        <div className={"col-span-2"}>
          <Card title={"DKIM"}>
            <Paragraph>
              To improve e-mail delivery, it is recommended to add the following TXT record as
              mail._domainkey.pekelvlees.nl in the DNS of the domain name.
            </Paragraph>
            <div
              className={
                "flex flex-col items-stretch space-y-4 lg:space-y-0 lg:space-x-4 lg:flex-row lg:items-center"
              }
            >
              <CopyableInput label={"Name"} value={`mail._domainkey.${mailDomain.displayName}`} />
              <CopyableInput
                label={"Content"}
                value={
                  "v=DKIM1; h=sha256; k=rsa; p=MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAuYp7LYsN4MvMwTwtmFdXsf3erJVISXtjUQeeAeL6PCJMVsmqLsVthi8u0zOkWHt9hnaAmxb4CL0MKPB5PxVozho4X9XAzXPTSdSb3ATMnb4bCbq7vpIBsDWsQJo93q5dA+eNyf/YmLWxSggaivXtI1FLhAQQ59d3RHEFU/Dxj4yVY5W7DH1/R3VR73tJwyyG6MM6iH+cm6PLPfDx+ZEMV9SlskTvIRRj5Zp38rJRrrrcIZFvMKEMe6lEN0phdjxiWuvrTKYLLlakqT3222ASJMqxMaHmSdm7Z2BWO40XYE1UE6dP8o6O8qENlNxd8E/AXfVRup7bZPbcggsF0Sqd2wIDAQAB"
                }
              />
            </div>
          </Card>
        </div>
      </div>
      <SectionHeader>Contracts</SectionHeader>
      <ContractsOverview mailDomain={mailDomain} />
    </>
  );
};

function UsersOverview(props: { mailDomain: MailDomain }) {
  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);
  const deploymentDetails = props.mailDomain.deploymentDetails instanceof MailDomainDeploymentDetails ? props.mailDomain.deploymentDetails : null;
  const mailBoxes = deploymentDetails?.mailboxes ?? [];
  const forwards = deploymentDetails?.forwards ?? [];
  const { open: openAddModal } = useModal({ title: "Add user", body: <AddMailUserModal domain={props.mailDomain} /> });
  return (
    <div className={"mb-12 bg-white text-black rounded-sm my-4 border border-slate-100 p-2"}>
      <table className={"w-full table"}>
        <tbody>
          {mailBoxes.map((mailBox, k) => {
            const nForwards = forwards.filter((f) => f.email === mailBox.email).length;
            return (
              <UsersOverviewTableRow key={k} mailbox={mailBox} mailDomain={props.mailDomain} nForwards={nForwards} />
            );
          })}
        </tbody>
      </table>
      {can("create", "mail") && (
        <button
          className={"hover:bg-brand-50 text-brand-700 w-full text-sm px-4 py-3 rounded-b text-left"}
          onClick={() => openAddModal()}
        >
          <FontAwesomeIcon icon={faPlus} className={"mr-2"} />
          <span className={"font-medium"}>Add user</span>
        </button>
      )}
    </div>
  );
}

const UsersOverviewTableRow: FC<{ mailbox: MailBoxDeploymentDetails; mailDomain: MailDomain; nForwards: number }> = ({
  mailbox,
  mailDomain,
  nForwards,
}) => {
  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);
  const { open: openDeleteModal } = useModal({
    title: "Delete user",
    body: <DeleteMailUserModal domain={mailDomain} email={mailbox.email} />,
  });
  const { open: openResetModal } = useModal({
    title: "Reset password",
    body: <EditMailUserModal domain={mailDomain} email={mailbox.email} />,
  });
  const options = [
    ...(can("edit", "mail", mailDomain.permissions)
      ? [
          {
            label: "Reset password",
            icon: faKey,
            onClick: () => {
              openResetModal();
            },
          },
        ]
      : []),
    ...(can("delete", "mail", mailDomain.permissions)
      ? [
          {
            label: "Delete",
            icon: faTrash,
            onClick: () => {
              openDeleteModal();
            },
          },
        ]
      : []),
  ];
  return (
    <tr className={"text-sm h-12"}>
      <th className={"px-4 text-left"}>{mailbox.email}</th>
      <th className={"px-4 text-left w-full"}>
        {nForwards > 0 && (
          <>
            <HoverHint hint={"This mailbox has forwards configured, see 'Forwards and Aliases'"}>
              <FontAwesomeIcon icon={faMailForward} className={"mr-2 text-slate-500"} />
              <span className={"text-xs text-slate-500"}>
                {nForwards} {nForwards === 1 ? "forward" : "forwards"}
              </span>
            </HoverHint>
          </>
        )}
      </th>
      {options.length > 0 && (
        <td className={"px-4 text-right"}>
          <ContextMenu options={options} />
        </td>
      )}
    </tr>
  );
};

function ForwardsAliasesOverview(props: { mailDomain: MailDomain }) {
  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);
  const deploymentDetails = props.mailDomain.deploymentDetails instanceof MailDomainDeploymentDetails ? props.mailDomain.deploymentDetails : null;
  const forwards = deploymentDetails?.forwards ?? [];
  const aliases = deploymentDetails?.aliases ?? [];
  const forwardsAndAliases = [...forwards, ...aliases].sort((a, b) => a.email.localeCompare(b.email));
  const { open: openAddModal } = useModal({
    title: "Add forward or alias",
    body: <AddMailAliasOrForwardModal domain={props.mailDomain} />,
  });
  return (
    <div className={"mb-12 bg-white text-black rounded-sm my-4 border border-slate-100 p-2"}>
      <table className={"w-full table"}>
        <tbody>
          {forwardsAndAliases.map((forwardOrAlias, k) => (
            <ForwardsAliasesOverviewTableRow key={k} forwardOrAlias={forwardOrAlias} mailDomain={props.mailDomain} />
          ))}
        </tbody>
      </table>
      {/* Adding an alias needs edit, adding a forward needs create. The modal only offers the allowed types. */}
      {(can("edit", "mail") || can("create", "mail")) && (
        <button
          className={"hover:bg-brand-50 text-brand-700 w-full text-sm px-4 py-3 rounded-b text-left"}
          onClick={() => openAddModal()}
        >
          <FontAwesomeIcon icon={faPlus} className={"mr-2"} />
          <span className={"font-medium"}>Add forward or alias</span>
        </button>
      )}
    </div>
  );
}

const ForwardsAliasesOverviewTableRow: FC<{
  forwardOrAlias: MailForwardDeploymentDetails | MailAliasDeploymentDetails;
  mailDomain: MailDomain;
}> = ({ forwardOrAlias, mailDomain }) => {
  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);
  const { open: openDeleteAliasModal } = useModal({
    title: "Delete alias",
    body: (
      <DeleteMailAliasModal
        domain={mailDomain}
        aliasId={forwardOrAlias instanceof MailAliasDeploymentDetails ? forwardOrAlias.id : 0}
        aliasAddress={forwardOrAlias instanceof MailAliasDeploymentDetails ? forwardOrAlias.alias : ""}
      />
    ),
  });
  const { open: openDeleteForwardModal } = useModal({
    title: "Delete forward",
    body: (
      <DeleteMailForwardModal
        domain={mailDomain}
        forwardId={forwardOrAlias instanceof MailForwardDeploymentDetails ? forwardOrAlias.id : 0}
        forwardAddress={forwardOrAlias instanceof MailForwardDeploymentDetails ? forwardOrAlias.target : ""}
      />
    ),
  });
  const isAlias = forwardOrAlias instanceof MailAliasDeploymentDetails;
  // Deleting an alias is an edit of the mail domain, deleting a forward needs the delete permission.
  const options = can(isAlias ? "edit" : "delete", "mail", mailDomain.permissions)
    ? [
        {
          label: "Delete",
          icon: faTrash,
          onClick: isAlias ? openDeleteAliasModal : openDeleteForwardModal,
        },
      ]
    : [];
  if (isAlias) {
    return (
      <tr className={"text-sm h-12"}>
        <td className={"px-4"}>
          <div
            className={"text-brand-700 text-xs uppercase tracking-wide border border-brand-300 inline px-1 rounded-sm"}
          >
            Alias
          </div>
        </td>
        <th className={"px-4 text-left"}>{forwardOrAlias.alias}</th>
        <th className={"px-4 text-left"}>
          <FontAwesomeIcon icon={faArrowRight} />
        </th>
        <th className={"px-4 text-left w-full"}>{forwardOrAlias.email}</th>
        {options.length > 0 && (
          <td className={"px-4 text-right"}>
            <ContextMenu options={options} />
          </td>
        )}
      </tr>
    );
  } else {
    return (
      <tr className={"text-sm h-12"}>
        <td className={"px-4"}>
          <div
            className={"text-brand-700 text-xs uppercase tracking-wide border border-brand-300 inline px-1 rounded-sm"}
          >
            Forward
          </div>
        </td>
        <th className={"px-4 text-left"}>{forwardOrAlias.email}</th>
        <th className={"px-4 text-left"}>
          <FontAwesomeIcon icon={faArrowRight} />
        </th>
        <th className={"px-4 text-left"}>{forwardOrAlias.target}</th>
        {options.length > 0 && (
          <td className={"px-4 text-right"}>
            <ContextMenu options={options} />
          </td>
        )}
      </tr>
    );
  }
};

export function ContractsOverview(props: { mailDomain: MailDomain }) {
  return (
    <div className={"mb-8"}>
      <DataTable
        keyProperty={"id"}
        data={props.mailDomain.contracts}
        columns={[
          { property: "displayName", header: "Description" },
          {
            property: "renewalAt",
            header: "Next invoice",
            transform: (v, c) => {
              if (c.cancelledAt !== null) {
                return <span className={"bg-red-600 text-white font-medium text-xs px-2 rounded-full"}>Cancelled</span>;
              }
              return v.toLocaleDateString("nl");
            },
          },
          {
            property: "billingPeriod",
            header: "Invoice period",
            transform: (v) => `Every ${v} ${v > 1 ? "months" : "month"}`,
          },
          { property: "amount", header: "Amount (Excl. VAT)", transform: (v) => `€ ${(v / 100).toFixed(2)}` },
        ]}
      />
    </div>
  );
}
